import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React from 'react';

interface SortableWrapperProps {
  /** Ordered array of unique string IDs matching the useSortable ids of child items */
  items: string[];
  /** Called with resolved (oldIndex, newIndex) after a successful drag */
  onSortEnd: (oldIndex: number, newIndex: number) => void;
  children: React.ReactNode;
  /**
   * Optional renderer for the drag preview shown via DragOverlay. When provided,
   * the dragged element is rendered in a portal (position: fixed) so its
   * transform cannot trigger ancestor overflow / page scrollbars.
   */
  renderOverlay?: (activeId: string) => React.ReactNode;
}

/**
 * Lightweight wrapper around @dnd-kit for vertical-list sortable cases.
 * Each child item should use `useSortable({ id })` with an id from the `items` array.
 */
export const SortableWrapper: React.FC<SortableWrapperProps> = ({
  items,
  onSortEnd,
  children,
  renderOverlay,
}) => {
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex !== -1 && newIndex !== -1) {
      onSortEnd(oldIndex, newIndex);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      {renderOverlay && (
        <DragOverlay dropAnimation={null}>
          {activeId !== null ? renderOverlay(String(activeId)) : null}
        </DragOverlay>
      )}
    </DndContext>
  );
};
